/**
 * Frontend Sentry init (OCP-79). Mirrors the backend plugin: reads
 * DSN + release + environment from Vite-injected build-time vars,
 * no-op when DSN is absent.
 *
 * Build-time variables (set in Coolify's build environment so the
 * production bundle ships with them baked in — runtime can't read
 * them after Vite finishes):
 *   - `VITE_SENTRY_DSN`         — required to enable Sentry
 *   - `VITE_SENTRY_ENVIRONMENT` — e.g. `production` / `staging`
 *   - `VITE_GIT_SHA`            — release-tag matching the API server
 */
import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  Sentry.init({
    dsn,
    environment: (import.meta.env.VITE_SENTRY_ENVIRONMENT as string) ?? import.meta.env.MODE,
    release: (import.meta.env.VITE_GIT_SHA as string) ?? 'local-dev',
    // Browser-tracing on a low sample rate (5%) — operator surface
    // doesn't get heavy enough traffic to need full tracing budget.
    tracesSampleRate: 0.05,
    // Don't ship form-input values along with crashes — operator
    // forms may contain emails, passwords, beacon secrets, etc.
    sendDefaultPii: false,
  });
}

/** Sentry-react ErrorBoundary, with our app's fallback already wired. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/** Manual capture helper for places that swallow the error but want it logged anyway. */
export function captureError(err: unknown, extras?: Record<string, unknown>): void {
  const e = err instanceof Error ? err : new Error(String(err));
  Sentry.captureException(e, extras ? { extra: extras } : undefined);
}
